import { useMutation } from '@apollo/client';
import { EVisibility, EWorldMapInitialType, WorldMapInput } from '__generated__/globalTypes';
import {
    CreateWorldMap,
    CreateWorldMapVariables
} from 'graphql/mutation/__generated__/CreateWorldMap';
import { CREATE_WORLD_MAP_MUTATION } from 'graphql/mutation/worldMutation';
import { useWorldMapLocalStorage } from 'libs/hooks/ownership/useWorldMapLocalStorage';
import { ICoordinates } from 'react-give-me-map';
import { BLANK_STYLE } from 'static/config/stylesConfig';
import { v4 as uuid } from 'uuid';

interface CreateProps {
    title: string;
    style: string;
    backgroundImage: {
        url: string;
        coordinates: ICoordinates[];
    };
}

// These are copied on BE as well. Don't change them only here
const DEFAULT_BOUNDS_COORDINATES = {
    topLeft: {
        lat: 70.89529248340676,
        lng: -15.72129085899823
    },
    bottomRight: {
        lat: 36.956114300958916,
        lng: 47.19320342994726
    }
};

export const WORLD_MAP_INPUT_DEFAULT: WorldMapInput = {
    title: '',
    visibility: EVisibility.PUBLIC,
    initialType: EWorldMapInitialType.AUTOMATIC,
    groupMarkerBackground: '#000',
    groupMarkerColor: '#fff',
    showOnMapZoom: 16,
    autoHideIcons: true,
    initial: {
        useBounds: true,
        autoBounds: { ...DEFAULT_BOUNDS_COORDINATES, padding: 50 },
        bounds: DEFAULT_BOUNDS_COORDINATES
    },
    initialMobile: {
        useBounds: true,
        bounds: DEFAULT_BOUNDS_COORDINATES
    },
    fillCountryColor: '#C5320F',
    fillCountryOpacity: 0.4,
    defaultStyles: ['mapbox://styles/koudelka/cld4go9oi000k01p7wci751am']
};

export const useCreateMap = () => {
    const { storeTokenForMapId } = useWorldMapLocalStorage();

    const [createWorldMapMutation] = useMutation<CreateWorldMap, CreateWorldMapVariables>(
        CREATE_WORLD_MAP_MUTATION
    );

    function create(createProps?: CreateProps) {
        const additionalMapData = {
            ...(createProps?.title ? { title: createProps.title } : {}),
            ...(createProps?.style ? { defaultStyles: [createProps.style] } : {}),
            ...(createProps?.backgroundImage &&
            createProps.backgroundImage.coordinates &&
            createProps.style === BLANK_STYLE
                ? {
                      imageBackgroundUrl: createProps.backgroundImage.url,
                      imageBackgroundCoordinates: createProps.backgroundImage.coordinates,
                      initial: {
                          useBounds: true,
                          bounds: {
                              padding: 20,
                              topLeft: createProps.backgroundImage.coordinates[0],
                              bottomRight: createProps.backgroundImage.coordinates[2]
                          },
                          autoBounds: {
                              padding: 20,
                              topLeft: createProps.backgroundImage.coordinates[0],
                              bottomRight: createProps.backgroundImage.coordinates[2]
                          }
                      },
                      initialMobile: {
                          useBounds: true,
                          bounds: {
                              padding: 20,
                              topLeft: createProps.backgroundImage.coordinates[0],
                              bottomRight: createProps.backgroundImage.coordinates[2]
                          },
                          autoBounds: {
                              padding: 20,
                              topLeft: createProps.backgroundImage.coordinates[0],
                              bottomRight: createProps.backgroundImage.coordinates[2]
                          }
                      }
                  }
                : {})
        };

        return createWorldMapMutation({
            variables: {
                worldMapId: uuid(),
                map: {
                    ...WORLD_MAP_INPUT_DEFAULT,
                    ...additionalMapData
                }
            },
            onCompleted: (result) => {
                const mapId = result.createWorldMap.map.worldMapId;
                storeTokenForMapId(mapId, result.createWorldMap.token);
                return result;
            }
        });
    }

    return {
        create
    };
};
